/**
 * Login.
 *
 * The rule that shapes every message here: NEVER ENUMERATE USERS (security.md §11). "No account
 * with that address" and "wrong password" are the same failure on the wire and on screen. The cost
 * is a slightly less helpful error; the alternative is handing an attacker a membership oracle for
 * the entire customer base, one address at a time.
 *
 * Turnstile appears after three failures for an identifier or on elevated IP risk — not on the
 * first attempt, where it taxes every legitimate login to slow an attacker who has not yet started.
 *
 * The credential goes to Supabase Auth and nowhere else; the session it returns is what every API
 * call afterwards carries as a bearer (api/client.ts).
 *
 * ## EMAIL FIRST (Prompt 3B, ADR 0120)
 *
 * One field, then `/sso/discovery` decides what the second step is: a redirect to the identity
 * provider, or the password field. This replaces a screen that showed the password form to
 * everybody and offered SSO as a button underneath.
 *
 * Two things this file used to say are no longer true and are corrected rather than left:
 *
 *   * "No pre-flight lookup of our own" — there is one now, and it is not the surface widening the
 *     old comment feared. `/sso/discovery` answers `password` for an unknown domain, an unverified
 *     one, a connection mid-setup and an internal failure, all identically, so it discloses strictly
 *     less than Supabase's own `/auth/v1/sso` endpoint already does.
 *   * "`sso_enforced` is read by nothing" — layer 3c reads it on every authenticated request. So
 *     enforcement is real, and this screen has to stop offering a door the server will refuse.
 *
 * ## Why the password door is HIDDEN and not REMOVED under enforcement
 *
 * The organization owner is permanently exempt — it is the break-glass that makes enforcement safe
 * to offer at all. This screen cannot know who the owner is (there is no session yet), so removing
 * the password field entirely would make that exemption unreachable through the product and leave
 * an owner with a broken identity provider unable to sign in at all. It is hidden behind a quiet
 * link instead: out of the way for the colleagues it does not apply to, present for the one person
 * it does. The server refuses everyone else regardless of what this screen shows.
 */
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Callout, Card, Field } from '@flowza/ui'
// The SUBPATH, not the barrel. `@flowza/core`'s index re-exports the whole of core — the PDF
// engine's types, the security-event writer, the entitlement service — and pulling it onto the
// sign-in path measurably moved the shared browser chunk (the editor budget went from 19% headroom
// to 4% before this line was changed back). `packages/core/package.json` carries subpath exports
// for exactly this reason; `./branding` and `./envelopes/inbox` are the precedent.
import { domainOfEmail } from '@flowza/core/sso/domain'
// The same subpath rule as the line above, and the same one constant the api Worker's staff gate
// reads: one value, so the browser and the server cannot disagree about whether a second factor is
// asked for (ADR 0193).
import { SECOND_FACTOR_ENABLED } from '@flowza/core/auth/second-factor'
import { discover, type Discovery } from '../../api/discovery'
import { configStatus, getClient } from '../../supabase'
import { assurance, completeStepUp, MfaError } from '../../auth/mfa'
import { returnTo } from '../../auth/returnTo'
import { MfaChallenge } from './Mfa'
import { AuthLayout } from './AuthLayout'
import { MicrosoftButton } from './MicrosoftButton'

const UNIFORM_ERROR = 'That email and password combination did not work.'

/**
 * What a failed SSO attempt may say, and why it is allowed to be specific where the password error
 * is not.
 *
 * `UNIFORM_ERROR` exists because "no account with that address" is a membership oracle for the
 * customer base, one address at a time (security.md §11). This message is a fact about a DOMAIN,
 * not about an address: it says whether `contoso.com` has an identity provider configured here,
 * which is the same thing Supabase's own `/auth/v1/sso` endpoint answers to anyone who asks. It
 * reveals nothing about whether any particular person has an account.
 */
const NO_SSO_FOR_DOMAIN =
  'That email domain is not set up for single sign-on here. Sign in with your password, or ask ' +
  'whoever administers your organization to set it up.'

const SSO_NEEDS_EMAIL = 'Enter your work email address first, so we know which organization to ask.'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  /**
   * What discovery said, or null while we are still on step one.
   *
   * `passwordRevealed` is separate from it on purpose: under enforcement the password field is
   * hidden but reachable, so "what the server said" and "what this person has asked to see" are
   * two different facts and collapsing them would lose the owner's way in.
   */
  const [found, setFound] = useState<Discovery | null>(null)
  const [passwordRevealed, setPasswordRevealed] = useState(false)
  /**
   * The verified factor this sign-in still has to present, or null.
   *
   * Set only from Supabase's own `nextLevel`/`currentLevel` comparison, never inferred from "this
   * account has a factor" — holding one and having presented it on THIS session are different
   * facts, and treating them as the same is how a step-up gets skipped.
   */
  const [stepUpFactorId, setStepUpFactorId] = useState<string | null>(null)
  const [mfaError, setMfaError] = useState<string | null>(null)
  const config = configStatus()
  // `returnTo` rather than a prefix test written out here: `/adm` is guarded by `RequireSession`
  // too, and the inline version honoured only `/app` — so a staff member who opened `/adm` signed
  // in and landed on the tenant dashboard instead (ADR 0186).
  const destination = returnTo((location.state as { from?: unknown } | null)?.from)

  /** Step one: one field, then ask which lane this address belongs to. */
  async function onContinue(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const typed = email.trim()
    if (domainOfEmail(typed) === null) {
      setSsoError(SSO_NEEDS_EMAIL)
      return
    }
    setBusy(true)
    setSsoError(null)
    try {
      const answer = await discover(typed)
      setFound(answer)
      // `password` is the answer for everything that is not a live, verified SSO domain, so it is
      // also the answer that shows the password field straight away rather than making somebody
      // press a second button to reach the only door they have.
      if (answer.mode === 'password') {
        setPasswordRevealed(true)
        return
      }
      await handOff(answer)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Send them to their identity provider.
   *
   * Both lanes go through Supabase's `signInWithSSO`, which is what mints the session; the
   * difference is only what identifies the provider. A failure here falls back to the password
   * field rather than stranding them — the same rule `discover` follows, for the same reason.
   */
  async function handOff(answer: Discovery) {
    const client = getClient()
    if (client === null) return
    const domain = domainOfEmail(email.trim())
    if (domain === null) return
    const { data, error: failure } = await client.auth.signInWithSSO(
      answer.provider_id !== undefined ? { providerId: answer.provider_id } : { domain },
    )
    if (failure || !data?.url) {
      setSsoError(NO_SSO_FOR_DOMAIN)
      // Revealed on a failure ONLY when enforcement does not apply. Under enforcement the password
      // door stays behind its link even here: auto-revealing it would put a password field in
      // front of every colleague whose provider is having a bad morning, which is exactly the
      // audience enforcement is for. The owner still reaches it in one click.
      if (answer.sso_enforced !== true) setPasswordRevealed(true)
      return
    }
    // A top-level navigation, not a popup: the identity provider decides its own framing rules and
    // several refuse to render inside one.
    window.location.assign(data.url)
  }

  /** Step two, password lane. */
  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const client = getClient()
    if (client === null) return
    const form = new FormData(event.currentTarget)
    const password = String(form.get('password') ?? '')
    setBusy(true)
    try {
      const { error: failure } = await client.auth.signInWithPassword({
        email: email.trim(),
        password,
      })
      if (failure) {
        // One message for every cause: no such account, wrong password, suspended, locked.
        setError(UNIFORM_ERROR)
        return
      }
      setError(null)

      /*
       * A password is one factor. Whether it is ENOUGH is Supabase's answer, not ours: `nextLevel`
       * is `aal2` only when this account holds a verified factor, so an account without one is
       * never asked to step up and cannot be locked out of its own sign-in by this branch.
       *
       * A failure to ASK is treated as a failure to sign in. The tempting `catch` here lets somebody
       * through at aal1 when the assurance call is what broke, which silently downgrades every
       * surface that gates on a second factor — the org's `require_mfa` policy and the staff console
       * alike. Refusing is the safe direction and it is recoverable by retrying.
       *
       * ALL OF THAT IS SWITCHED OFF (ADR 0193), and the question is not asked at all rather than
       * asked and ignored: an account holding a verified factor is admitted on its password, which
       * is the founder's instruction of 2026-09-16 and is why `admin@acme.com` can sign in here
       * again. The whole block is inside the flag so that a build with the second factor back on
       * keeps the refuse-on-failure property above — it is only safe to skip the call when nothing
       * downstream reads its answer.
       */
      if (SECOND_FACTOR_ENABLED) {
        let next
        try {
          next = await assurance()
        } catch {
          setError(UNIFORM_ERROR)
          await client.auth.signOut().catch(() => undefined)
          return
        }

        if (next.stepUpRequired && next.factorId !== null) {
          setStepUpFactorId(next.factorId)
          return
        }
      }
      navigate(destination, { replace: true })
    } finally {
      setBusy(false)
    }
  }

  /** Step three, when this account has a second factor. */
  async function onStepUp(code: string) {
    if (stepUpFactorId === null) return
    setBusy(true)
    setMfaError(null)
    try {
      await completeStepUp(stepUpFactorId, code)
      navigate(destination, { replace: true })
    } catch (caught) {
      setMfaError(
        caught instanceof MfaError || caught instanceof Error
          ? caught.message
          : 'that code was not accepted',
      )
    } finally {
      setBusy(false)
    }
  }

  const enforced = found?.sso_enforced === true
  const federated = found !== null && found.mode !== 'password'

  /*
   * The second factor replaces the whole screen rather than appearing under the password field, and
   * the reason is that the password is SPENT: the session exists at aal1 already. Leaving the email
   * and password form on screen invites somebody to retype a credential that has already been
   * accepted, and a second `signInWithPassword` would throw the half-finished sign-in away.
   *
   * `MfaChallenge` brings its own page furniture, so it is returned instead of `AuthLayout` rather
   * than inside it.
   */
  if (stepUpFactorId !== null) {
    return <MfaChallenge onSubmit={(code) => void onStepUp(code)} busy={busy} error={mfaError} />
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle={
        found === null
          ? 'Enter your work email and we will take you to the right place.'
          : federated
            ? 'Your organization signs in through its own identity provider.'
            : 'Welcome back.'
      }
      footer={
        <>
          New to FlowZa Sign?{' '}
          <Link to="/auth/signup" className="text-accent font-medium">
            Create an account
          </Link>
        </>
      }
    >
      {!config.ok ? (
        <div className="mb-4">
          <Callout tone="danger" title="This deployment is not configured">
            Missing build variables: {config.missing.join(', ')}. Signing in needs them.
          </Callout>
        </div>
      ) : null}

      <Card>
        {!passwordRevealed ? (
          <form className="flex flex-col gap-4" onSubmit={(e) => void onContinue(e)}>
            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (ssoError !== null) setSsoError(null)
                // A changed address invalidates the answer. Without this, editing the domain after
                // a lookup would sign somebody in through the previous domain's provider.
                if (found !== null) setFound(null)
              }}
            />
            <Button type="submit" disabled={busy || !config.ok} className="mt-1 w-full">
              {busy ? 'Checking…' : 'Continue'}
            </Button>
            {ssoError !== null ? (
              <p className="text-danger m-0 text-sm" role="alert">
                {ssoError}
              </p>
            ) : null}
          </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              {...(error ? { error } : {})}
            />
            <Button type="submit" disabled={busy || !config.ok} className="mt-1 w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            {/*
              Carried onto step two, because this form is sometimes reached BY a hand-off failure.
              Dropping it here left somebody staring at a password field with no explanation of why
              the door they chose did not open — found by the test for that message, which went
              looking for it and could not find it.
            */}
            {ssoError !== null ? (
              <p className="text-danger m-0 text-sm" role="alert">
                {ssoError}
              </p>
            ) : null}
            {enforced ? (
              <p className="text-text-muted m-0 text-xs">
                Your organization requires single sign-on. A password works only for the account
                owner.
              </p>
            ) : null}
          </form>
        )}

        {federated && !passwordRevealed ? (
          <div className="border-border-subtle mt-5 border-t pt-5">
            <p className="text-text-muted m-0 mb-3 text-sm">
              Taking you to your organization&rsquo;s sign-in…
            </p>
            <Button
              variant="secondary"
              onClick={() => void handOff(found)}
              disabled={busy || !config.ok}
              className="w-full"
            >
              Continue with your organization
            </Button>
            {ssoError !== null ? (
              <p className="text-danger m-0 mt-3 text-sm" role="alert">
                {ssoError}
              </p>
            ) : null}
            {/*
              HIDDEN, not removed — see the header. The owner exemption is what makes enforcement
              safe to offer, and this screen has no session and so cannot know who the owner is. A
              quiet link keeps the break-glass reachable without inviting everybody else to press
              it; the server refuses them regardless of what this shows.
            */}
            <button
              type="button"
              className="text-text-muted mt-3 w-full text-center text-xs underline"
              onClick={() => setPasswordRevealed(true)}
            >
              {enforced ? 'I am the account owner' : 'Use a password instead'}
            </button>
          </div>
        ) : null}

        {/*
          The Microsoft door (ADR 0093). Only on step one: once discovery has answered, showing a
          third door would invite somebody to take a lane their organization did not configure.
        */}
        {found === null ? (
          <MicrosoftButton
            intro="Or use the account you already sign in to work with."
            disabled={busy || !config.ok}
          />
        ) : null}
      </Card>

      {/*
        The dead-end ban (ux.md §12): every failure state offers a way forward.
      */}
      <p className="text-text-muted mt-4 text-sm">
        <Link to="/auth/reset" className="text-accent font-medium">
          Forgotten your password?
        </Link>
      </p>
    </AuthLayout>
  )
}
